import { Vector2 } from '../../odie';
import { arrayRandom } from '../../lib/util/jsTools';
import type { MapComponent } from '../components/MapComponent';
import { config } from '../defs/config';
import type { BlockEntity } from '../entities/BlockEntity';
import { moveBlockEntity, spawnBlockEntity } from '../entities/BlockEntity';
import type { GameScene } from '../GameScene';
import { findDropRow, isCellBarrier, isDroppableCell } from './mapTools';
import { BlockId, blockPropsMap, powerBlockTypes } from '../defs/block';
import { BlockGenAlgorithm } from '../defs/map';
import { getBlockGen } from '../../match2.util';
import { blockGeneration } from './blockGeneration';
import {
  getPowerupSpawnChance,
  getSpecialSpawnChance,
} from './blockGenerationSpecial';
import { matchCheckTappable } from './matchTools';

// types
//-----------------------------------------------------------------------------
export type FallAction = {
  entity: BlockEntity;
  fromRow: number;
  toRow: number;
};

/*
    falls all blocks in map. produces all actions taken.
*/
export class MapFaller {
  // fields
  //-------------------------------------------------------------------------
  private readonly _scene: GameScene;
  private readonly _map: MapComponent;
  private readonly _actions: FallAction[] = [];

  // init
  //-------------------------------------------------------------------------
  constructor(scene: GameScene) {
    this._scene = scene;
    this._map = scene.sessionEntity.c.map;
  }

  // api
  //-------------------------------------------------------------------------
  public build(): FallAction[] {
    // build fall actions from existing blocks
    this._buildCurrent();

    // build new spawn actions
    this._buildNew();

    return this._actions;
  }

  // api
  //-------------------------------------------------------------------------
  private _buildCurrent() {
    // for each row from the bottom
    for (let row = this._map.rows - 1; row >= 0; --row) {
      // for each column
      for (let column = 0; column < this._map.columns; ++column) {
        const cell = this._map.grid[column][row];

        if (isDroppableCell(this._scene, cell)) {
          const entity = cell.base?.entity;
          if (!entity) continue;
          let toRow = findDropRow(this._map, entity);

          const overlay = cell.overlay?.entity;
          // If the block has a mobile overlay, check if that overlay can fall too
          if (toRow && overlay && !overlay.c.block.props.immobile) {
            // Find the landing row for the overlay
            const toOverlayRow = findDropRow(this._map, overlay);
            if (toOverlayRow !== undefined) {
              // The minimum of the two rows is the one the block can fall to
              toRow = Math.min(toRow, toOverlayRow);
            } else {
              continue;
            }
          }

          // if target row available
          if (toRow !== undefined) {
            // move entity there
            moveBlockEntity(this._scene, entity, new Vector2(column, toRow));

            // add fall action
            this._actions.push({
              entity,
              fromRow: row,
              toRow,
            });

            // for mobile overlays
            if (overlay && !overlay.c.block.props.immobile) {
              // move overlay entity to the same location
              moveBlockEntity(this._scene, overlay, new Vector2(column, toRow));

              // add fall action
              this._actions.push({
                entity: overlay,
                fromRow: row,
                toRow,
              });
            }
          }
        }
      }
    }
  }

  private _buildNew() {
    // List of all spawn locations for newly spawned blocks
    const spawnList: { column: number; row: number; fromRow: number }[] = [];

    // for each column
    for (let column = 0; column < this._map.columns; ++column) {
      const columns = this._map.grid[column];
      const dropRows: number[] = [];

      // for each row until entity
      for (let row = 0; row < this._map.rows; ++row) {
        const cell = columns[row];

        // if enabled
        if (cell.enabled) {
          // end at first barrier
          if (isCellBarrier(cell)) {
            break;
          }

          // add drop row
          dropRows.push(row);
        }
      }

      // for each row in reverse
      let fromRow = config.map.spawnRow;

      for (const row of dropRows.reverse()) {
        // Fill up the list of spawn locations, but don't spawn yet
        spawnList.push({ column, row, fromRow });
        --fromRow;
      }
    }

    const totalObstacles = this._scene.getTotalObstacleGoals();
    const destroyedObstacles =
      totalObstacles - this._scene.getRemainingObstacles();
    const obstacleCompletion = Math.round(
      (destroyedObstacles / totalObstacles) * 100,
    );
    const moveCompletion = Math.round(
      (1 - this._scene.moves / this._scene.mapDef!.moves) * 100,
    );

    let blockGen = getBlockGen(this._map.mapDef, this._scene.level, {
      obstacleCompletion,
      moveCompletion,
      continues: this._scene.continues,
    });

    // Override block generation algorithm if there is a cheat enforcing it
    let cheatAlgorithm = this._scene.cheatSystem?.getBlockGenAlgorithm();
    if (cheatAlgorithm) {
      blockGen = cheatAlgorithm;
    }

    this._scene.cheatSystem?.setObstacleCompletion(obstacleCompletion);
    this._scene.cheatSystem?.setMoveCompletion(moveCompletion);
    this._scene.cheatSystem?.setDynamicAlgorithm(blockGen);
    this._scene.dynamicBlockGenType = blockGen;

    if (blockGen !== 'random') {
      // Sort the spawn list by row, so it will spawn from the bottom up
      spawnList.sort((a, b) => b.row - a.row);
    }

    let groupBaseColor: BlockId | undefined;

    if (blockGen === 'easy-v3-group-spawns') {
      // pick a random spanwable color
      groupBaseColor = arrayRandom(this._scene.getSpawnListBasic()) as BlockId;
    }

    // Spawn all the blocks
    for (const item of spawnList) {
      const entity = this._spawnFallBlock(
        item.column,
        item.row,
        blockGen,
        groupBaseColor,
      );
      if (entity) {
        this._actions.push({
          entity,
          fromRow: item.fromRow,
          toRow: item.row,
        });
      }
    }
  }

  // private: spawners
  //-------------------------------------------------------------------------
  private _spawnFallBlock(
    column: number,
    row: number,
    blockGen: BlockGenAlgorithm,
    groupBaseColor?: BlockId,
  ): BlockEntity | undefined {
    const spawns = this._getSpawnList(column, row, blockGen, groupBaseColor);

    // require have spawns
    if (spawns.length === 0) {
      return undefined;
    }

    // choose block id
    const id = arrayRandom(spawns) as BlockId;

    // spawn entity
    return spawnBlockEntity(this._scene, { id }, new Vector2(column, row));
  }

  private _getSpawnList(
    column: number,
    row: number,
    blockGen: BlockGenAlgorithm,
    groupBaseColor?: BlockId,
  ): BlockId[] {
    if (!blockGeneration[blockGen]) {
      throw new Error(`Unknown blockGen algorithm: ${blockGen}`);
    }

    // Get special spawns list - can be empty if there are no special spawns available or if it doesn't pass the chances
    const specialSpawns = this.getSpecialSpawnList();
    if (specialSpawns.length > 0) {
      return specialSpawns;
    }

    // Get powerup spawns list - can be empty if there are no powerup spawns available or if it doesn't pass the chances
    const powerupSpawns = this.getPowerupSpawnList();
    if (powerupSpawns.length > 0) {
      return powerupSpawns;
    }

    // Check basic blocks in the grid, and trigger the 'ensure-match' algorithm if needed,
    // that should guarantee at least a match if spawning 2 or more blocks.
    const numBasicBlocks = this._scene.countBasicBlocks();
    const numBasicSpawnableBlocks = this._scene.getSpawnListBasic().length;
    const maxBlocksToTrigger = numBasicSpawnableBlocks || 5;
    const hasTappableMatches = matchCheckTappable(this._scene);
    if (numBasicBlocks < maxBlocksToTrigger && !hasTappableMatches) {
      blockGen = 'ensure-match';
    }

    // Return basic spawns list
    return blockGeneration[blockGen](
      this._scene,
      this._map,
      column,
      row,
      blockGen,
      groupBaseColor,
    );
  }

  /**
   * Retrieve a list of special (obstacles) spawns, if available and passed the chances, otherwise return empty.
   */
  private getSpecialSpawnList(): BlockId[] {
    // This is a bit different from the original implementation, should pick a single block first
    const spawns = this._scene.getSpawnListSpecial();

    // Return empty if there are no special spawns available
    if (!spawns.length) return [];

    // Pick one of the special spawns available
    const blockId = arrayRandom(spawns) as BlockId;

    // Calculate the chance of spawning the special block, based on the number of basic blocks, obstacles and remaining goals
    const chance = getSpecialSpawnChance({
      numBasicBlocks: this._scene.countBasicBlocks(),
      numObstacles: this._scene.countBlocks(blockId),
      remainingGoal: this._scene.getGoalCount(blockId),
    });

    // Return the block if it passes the chance, otherwise return empty
    if (Math.random() < chance) {
      return [blockId];
    } else {
      return [];
    }
  }

  /**
   * Retrieve a list of powerup spawns, if available and passed the chances, otherwise return empty.
   */
  private getPowerupSpawnList(): BlockId[] {
    const spawns = this._scene.getSpawnListPowerup();
    // Return empty if there are no powerup spawns available
    if (!spawns.length) return [];

    // Final list that the powerup will be picked from
    const selectedSpawns: BlockId[] = [];

    // Because rockets and cubes (lollipop) can have more than one entry in the list, we need to pick one of them first
    // So they will not have a better chance of spawning than the bomb, for instance.
    for (const powerupType of powerBlockTypes) {
      const powerups = spawns.filter(
        (id) => blockPropsMap[id].type === powerupType,
      );
      if (powerups.length) {
        selectedSpawns.push(arrayRandom(powerups) as BlockId);
      }
    }

    // Pick one of the special spawns available
    const blockId = arrayRandom(selectedSpawns) as BlockId;
    const chance = getPowerupSpawnChance({
      numBasicBlocks: this._scene.countBasicBlocks(),
      numPowerups: this._scene.countPowerupBlocks(),
    });
    // Return the block if it passes the chance, otherwise return empty
    if (Math.random() < chance) {
      return [blockId];
    } else {
      return [];
    }
  }
}
